import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Subject, takeUntil } from 'rxjs';
import { AlertService } from 'src/app/components/alert/alert.service';
import { PolicyService } from 'src/app/modules/policy/policy.service';
import { CLOSE_BUTTON, CONFIRM_BUTTON, IModalButton } from 'src/app/modules/shared/components/basic-modal/basic-modal.constants';
import { IFormKey } from 'src/app/modules/shared/models/forms';
import { IObjectKey } from 'src/app/modules/shared/models/objects';
import { DATE_FORMAT_FROM_BE, ESubjectType, IBeneficiaryObject, ICityObject, ICountriesObject, IProvinceObject } from 'src/app/modules/shared/models/policy.model';
import { LoggerService } from 'src/app/services/logger.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { CommonUtils } from 'src/app/utils/CommonUtils';
import { FormUtils } from 'src/app/utils/FormUtils';
import { IAnagrafica } from '../../../shared/components/ndg-variation/ndg-variation.constants';
import { AthoraService } from '../../athora.service';
import {
  additional_subject_info_form_keys,
  additional_subject_info_form_template,
  additional_subject_info_form_validators,
  search_subject_form_keys,
  search_subject_form_template,
  search_subject_form_validators,
  SIMOR_individual_form_keys,
  SIMOR_individual_form_template,
  SIMOR_individual_form_validators,
  SIMOR_legal_person_form_keys,
  SIMOR_legal_person_form_template,
  SIMOR_legal_person_form_validators
} from '../claim-notification/claim-notification.constants';
import { CODICE_NAZIONE_ITALIA, contractor_keys_PF, contractor_keys_PG, SIGLA_CAP_STATO_ESTERO, SIGLA_CITTA_STATO_ESTERO, SIGLA_PROVINCIA_STATO_ESTERO } from '../contractor-variation/contractor-variation.constants';
import {
  birth_form_enabling_map,
  birth_form_keys,
  birth_form_template,
  birth_form_validators,
  birth_hidden_field_foreign_state,
  EBeneficiaryType,
  ERelationshipWithSubscriberDesc,
  ESpecificBeneficiaryType,
  hidden_field_foreign_state,
  individual_form_keys,
  individual_form_template,
  individual_form_validators,
  legal_person_form_keys,
  legal_person_form_template,
  legal_person_form_validators,
  residence_enabling_map,
  residence_form_keys,
  residence_form_template,
  residence_form_validators,
  subject_type_form_keys,
  subject_type_form_template,
  subject_type_form_validators
} from './beneficiary-form.constants';
import moment = require('moment');

@Component({
  selector: 'app-beneficiary-form',
  templateUrl: './beneficiary-form.component.html',
  styleUrl: './beneficiary-form.component.scss'
})
export class BeneficiaryFormComponent implements OnInit, OnDestroy {

  subjectPFFields: string[];
  subjectPGFields: string[];
  isSimor: boolean;
  beneficiary: IBeneficiaryObject;
  searchSubjectForm: FormGroup;
  searchSubjectFormKeys: IFormKey[];
  searchedSubject: IAnagrafica = null;
  selectedIndex: number;
  additionalSubjectInfoForm: FormGroup;
  additionalSubjectInfoFormKeys: IFormKey[];
  buttons: IModalButton[] = [CLOSE_BUTTON, CONFIRM_BUTTON];
  countriesList: ICountriesObject[];
  birthProvincesList: IProvinceObject[];
  birthCitiesList: ICityObject[];
  residenceProvincesList: IProvinceObject[];
  residenceCitiesList: ICityObject[];
  isReadOnly: boolean;
  destroy$ = new Subject();

  // form tipologia beneficiario
  subjectTypeForm: FormGroup;
  subjectTypeFormKeys: IFormKey[] = CommonUtils.assign(subject_type_form_keys);

  // form dati generici beneficiario PF
  individualForm: FormGroup;
  individualFormTemplate;
  individualFormValidators;
  individualFormKeys: IFormKey[];

  // form dati generici beneficiario PG
  legalPersonForm: FormGroup;
  legalPersonFormTemplate;
  legalPersonFormValidators;
  legalPersonFormKeys: IFormKey[];

  // form dati di residenza del beneficiario
  residenceForm: FormGroup;
  residenceFormKeys: IFormKey[] = CommonUtils.assign(residence_form_keys);

  // form dati di nascita del beneficiario
  birthForm: FormGroup;
  birthFormKeys: IFormKey[] = CommonUtils.assign(birth_form_keys);

  constructor(
    public dialogRef: MatDialogRef<BeneficiaryFormComponent>,
    @Inject(MAT_DIALOG_DATA) public modalData,
    private readonly _policyService: PolicyService,
    private readonly _athoraService: AthoraService,
    private readonly _spinnerService: SpinnerService,
    private readonly _alertService: AlertService
  ) { }

  ngOnInit(): void {
    this.manageInputObjects();
    this.manageForms();
    this.watchForm();
  }

  manageInputObjects(): void {
    this.countriesList = this.modalData.countriesList;
    this.birthCitiesList = this.modalData.birthCitiesList;
    this.birthProvincesList = this.modalData.birthProvincesList;
    this.birthCitiesList = this.modalData.birthCitiesList;
    this.residenceProvincesList = this.modalData.residenceProvincesList;
    this.residenceCitiesList = this.modalData.residenceCitiesList;
    this.isReadOnly = this.modalData.isReadOnly;
    this.isSimor = this.modalData.isSimor;
    this.individualFormTemplate = this.isSimor ? SIMOR_individual_form_template : individual_form_template;
    this.individualFormValidators = this.isSimor ? SIMOR_individual_form_validators : individual_form_validators;
    this.individualFormKeys = this.isSimor ? CommonUtils.assign(SIMOR_individual_form_keys) : CommonUtils.assign(individual_form_keys);
    this.legalPersonFormTemplate = this.isSimor ? SIMOR_legal_person_form_template : legal_person_form_template;
    this.legalPersonFormValidators = this.isSimor ? SIMOR_legal_person_form_validators : legal_person_form_validators;
    this.legalPersonFormKeys = this.isSimor ? CommonUtils.assign(SIMOR_legal_person_form_keys) : CommonUtils.assign(legal_person_form_keys);
  }

  manageForms(): void {
    this.beneficiary = this.modalData.beneficiary;
    this.subjectTypeForm = FormUtils.initFormGroupFromTemplate(subject_type_form_template, subject_type_form_validators);
    if (this.isSimor) {
      // gestione form libero
      this.subjectTypeForm.get('beneficiaryType').patchValue(EBeneficiaryType.SPECIFIC_BENEFICIARY);
      this.subjectTypeForm.get('beneficiaryType').disable();
      this.subjectTypeFormKeys.find(key => key.controlName === 'specificBeneficiaryType').hidden = false;
      // gestione form ricerca soggetto
      this.searchSubjectForm = FormUtils.initFormGroupFromTemplate(search_subject_form_template, search_subject_form_validators);
      this.searchSubjectFormKeys = search_subject_form_keys;
    }
    if (this.beneficiary) {
      this.manageInputBeneficiary();
    }
  }

  manageInputBeneficiary(): void {
    LoggerService.logInfo(this.constructor.name, 'manageInputBeneficiary', { beneficiary: this.beneficiary });
    const isLegalSubject: boolean = this.beneficiary.isLegalSubject === 'S';
    this.manageSubjectTypeForm(isLegalSubject);
    if (!isLegalSubject) {
      this.manageIndividualForms();
    } else {
      this.manageLegalPersonForms();
    }
    this.manageResidenceForm();
    if (this.isReadOnly) {
      this.subjectTypeForm?.disable();
      this.residenceForm?.disable();
      this.birthForm?.disable();
      this.individualForm?.disable();
      this.legalPersonForm?.disable();
    } else {
      if (this.birthForm) {
        this.watchBirthForm();
      }
      this.watchResidenceForm();
    }
  }

  manageSubjectTypeForm(isLegalSubject: boolean): void {
    this.subjectTypeForm.get('beneficiaryType').patchValue(EBeneficiaryType.SPECIFIC_BENEFICIARY);
    this.subjectTypeForm.get('beneficiaryType').disable();
    this.subjectTypeForm.get('genericBeneficiary').patchValue(null);
    this.subjectTypeForm.get('specificBeneficiaryType').patchValue(isLegalSubject? ESpecificBeneficiaryType.PERSONA_GIURIDICA : ESpecificBeneficiaryType.PERSONA_FISICA);
    this.subjectTypeForm.get('specificBeneficiaryType').disable();
    this.subjectTypeFormKeys.find(key => key.controlName === 'specificBeneficiaryType').hidden = false;
  }

  manageIndividualForms(): void {
    const birthInfo = {
      birthCountry: this.beneficiary.birthCountry,
      birthProvince: this.beneficiary.birthProvince,
      birthPlace: this.beneficiary.birthPlace,
    }
    this.birthForm = FormUtils.initFormGroupFromTemplate(birthInfo, birth_form_validators);
    const birthCountryKey = this.birthFormKeys.find(key => key.controlName === 'birthCountry');
    if (birthCountryKey) {
      birthCountryKey.type = 'SELECT';
      birthCountryKey.options = this.countriesList.map(x => ({
        key: x.countryBDICode,
        value: x.countryDescription
      }));
    }
    if (this.birthCitiesList && this.birthProvincesList) {
      const birthProvinceKey = this.birthFormKeys.find(key => key.controlName === 'birthProvince');
      if (birthProvinceKey) {
        birthProvinceKey.type = 'SELECT';
        birthProvinceKey.options = this.birthProvincesList.map(x => ({
          key: x.provinceCode,
          value: x.provinceDescription
        }));
      }
      const birthPlaceKey = this.birthFormKeys.find(key => key.controlName === 'birthPlace');
      if (birthPlaceKey) {
        birthPlaceKey.type = 'SELECT';
        birthPlaceKey.options = this.birthCitiesList.map(x => ({
          key: x.cityName,
          value: x.cityName
        }));
      }
    } else {
      this.manageBirthForeignState();
    }
    const individualInfo = {
      beneficiaryTypeDesc: {value: 'Morte', disabled: true},
      taxCode: this.beneficiary.taxCode,
      name: this.beneficiary.name,
      surname: this.beneficiary.surname,
      birthDate: moment(this.beneficiary.birthDate, DATE_FORMAT_FROM_BE),
      citizenship: this.beneficiary.citizenship,
      sex: this.beneficiary.sex,
      quote: this.beneficiary.quote,
      relationshipWithSubscriber: Object.keys(ERelationshipWithSubscriberDesc).find(key => ERelationshipWithSubscriberDesc[key as keyof typeof ERelationshipWithSubscriberDesc] === this.beneficiary.relationshipWithSubscriber),
      iban: this.isSimor ? this.beneficiary.iban : null
    }
    this.individualForm = FormUtils.initFormGroupFromTemplate(individualInfo, individual_form_validators);
    const citizenshipKey = this.individualFormKeys.find(key => key.controlName === 'citizenship');
    if (citizenshipKey) {
      citizenshipKey.type = 'SELECT';
      citizenshipKey.options = this.countriesList.map(x => ({
        key: x.countryBDICode,
        value: x.countryDescription
      }));
    }
  }

  manageLegalPersonForms(): void {
    const legalPersonInfo = {
      beneficiaryTypeDesc: {value: 'Morte', disabled: true},
      birthDate: moment(this.beneficiary.birthDate, DATE_FORMAT_FROM_BE),
      vatCode: this.beneficiary.vatCode,
      shareCapital: this.beneficiary.shareCapital,
      companyName: this.beneficiary.companyName,
      quote: this.beneficiary.quote,
      relationshipWithSubscriber: Object.keys(ERelationshipWithSubscriberDesc).find(key => ERelationshipWithSubscriberDesc[key as keyof typeof ERelationshipWithSubscriberDesc] === this.beneficiary.relationshipWithSubscriber),
      iban: this.isSimor ? this.beneficiary.iban : null
    }
    this.legalPersonForm = FormUtils.initFormGroupFromTemplate(legalPersonInfo, legal_person_form_validators);
  }

  manageResidenceForm(): void {
    const residenceInfo = {
      country: this.beneficiary.residenceCountry,
      province: this.beneficiary.residenceProvince,
      place: this.beneficiary.residencePlace,
      toponym: this.beneficiary.residenceToponym,
      address: this.beneficiary.residenceAddress,
      addressNumber: this.beneficiary.residenceAddressNumber,
      postalCode: this.beneficiary.residencePostalCode
    }
    this.residenceForm = FormUtils.initFormGroupFromTemplate(residenceInfo, residence_form_validators);
    const residenceCountryKey = this.residenceFormKeys.find(key => key.controlName === 'country');
    if (residenceCountryKey) {
      residenceCountryKey.type = 'SELECT';
      residenceCountryKey.options = this.countriesList.map(x => ({
        key: x.countryBDICode,
        value: x.countryDescription
      }));
    }
    if (this.residenceProvincesList && this.residenceCitiesList) {
      const residenceProvinceKey = this.residenceFormKeys.find(key => key.controlName === 'province');
      if (residenceProvinceKey) {
        residenceProvinceKey.type = 'SELECT';
        residenceProvinceKey.options = this.residenceProvincesList.map(x => ({
          key: x.provinceCode,
          value: x.provinceDescription
        }));
      }
      const residencePlaceKey = this.residenceFormKeys.find(key => key.controlName === 'place');
      if (residencePlaceKey) {
        residencePlaceKey.type = 'SELECT';
        residencePlaceKey.options = this.residenceCitiesList.map(x => ({
          key: x.cityName,
          value: x.cityName
        }));
      }
    } else {
      this.manageResidenceForeignState(true);
    }
  }

  watchForm(): void {
    if (this.subjectTypeForm) {
      Object.keys(subject_type_form_template).forEach(controlName => {
        this.subjectTypeForm.get(controlName).valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe(value => {
            LoggerService.logInfo(this.constructor.name, 'watchForm', { controlName, value });
            if (controlName === 'beneficiaryType') {
              this.manageBeneficiaryType(value);
            } else if (controlName === 'specificBeneficiaryType' && value) {
              this.manageSpecificBeneficiaryType(value);
            }
          });
      });
    }
  }

  watchResidenceForm(): void {
    Object.keys(residence_enabling_map).forEach(controlName => {
      this.residenceForm.get(controlName).valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          if (!this.isResidenceForeignState) {
            const childControlName = residence_enabling_map[controlName];
            const control = this.residenceForm.get(childControlName);
            if (value && this.residenceForm.get(controlName)) {
              control.enable();
              control.reset();
              control.setValidators(residence_form_validators[childControlName]);
            } else {
              control.reset();
              control.disable();
            }
          }
        })
    })
    this.residenceForm.get('country').valueChanges
    .pipe(takeUntil(this.destroy$))
    .subscribe(value => {
      if (value) {
        if (this.isResidenceForeignState) {
          this.manageResidenceForeignState(false);
        } else {
          this.residenceForm.get('toponym').setValidators(residence_form_validators['toponym']);
          this.retrieveProvinces('residenceFormKeys', 'province');
        }
      }
    })

    this.residenceForm.get('province').valueChanges
    .pipe(takeUntil(this.destroy$))
    .subscribe(value => {
      if (value && !this.isResidenceForeignState) {
        this.retrieveCities('residenceFormKeys', value, 'place');
      }
    })
  }

  watchBirthForm(): void {
    Object.keys(birth_form_enabling_map).forEach(controlName => {
      this.birthForm.get(controlName).valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          if (!this.isBirthForeignState) {
            const childControlName = birth_form_enabling_map[controlName];
            const control = this.birthForm.get(childControlName);
            if (value && this.birthForm.get(controlName)) {
              control.enable();
              control.reset();
              control.setValidators(birth_form_validators[childControlName]);
            } else {
              control.reset();
              control.disable();
            }
          }
        })
    })
    this.birthForm.get('birthCountry').valueChanges
    .pipe(takeUntil(this.destroy$))
    .subscribe(value => {
      if (value) {
        if (this.isBirthForeignState) {
          this.manageBirthForeignState();
        } else {
          this.retrieveProvinces('birthFormKeys', 'birthProvince');
        }
      }
    })

    this.birthForm.get('birthProvince').valueChanges
    .pipe(takeUntil(this.destroy$))
    .subscribe(value => {
      if (value && !this.isBirthForeignState) {
        this.retrieveCities('birthFormKeys', value, 'birthPlace');
      }
    })
  }

  setControlVisibilityAndValidators(key: any, condition: boolean): void {
    key.hidden = condition;
    if (!key.hidden && !this.subjectTypeForm.get(key.controlName).disabled) {
      this.resetControl(key.controlName);
    }
    const validator = key.hidden ? null : subject_type_form_validators[key.controlName];
    this.subjectTypeForm.get(key.controlName).setValidators(validator);
  }

  resetControl(controlName: string): void {
    const control = this.subjectTypeForm.get(controlName);
    if (control) {
      control.reset();
      control.markAsUntouched();
    }
  }

  manageBeneficiaryType(value: string): void {
    this.subjectTypeFormKeys.forEach(key => {
      if (key.controlName === 'genericBeneficiary') {
        this.setControlVisibilityAndValidators(key, value === EBeneficiaryType.SPECIFIC_BENEFICIARY);
      } else if (key.controlName === 'specificBeneficiaryType') {
        this.setControlVisibilityAndValidators(key, value === EBeneficiaryType.GENERIC_BENEFICIARY);
      }
    });
  }

  manageSpecificBeneficiaryType(value: string): void {
    if (value === ESpecificBeneficiaryType.PERSONA_FISICA) {
      // gestione form nascita
      this.birthForm = FormUtils.initFormGroupFromTemplate(birth_form_template, birth_form_validators);
      const birthCountryKey = this.birthFormKeys.find(key => key.controlName === 'birthCountry');
      if (birthCountryKey) {
        birthCountryKey.type = 'SELECT';
        birthCountryKey.options = this.countriesList.map(x => ({
          key: x.countryBDICode,
          value: x.countryDescription
        }));
      }
      this.watchBirthForm();
      // gestione form dati generali
      this.individualForm = FormUtils.initFormGroupFromTemplate(this.individualFormTemplate, this.individualFormValidators);
      if (this.individualFormKeys) {
        const citizenshipKey = this.individualFormKeys.find(key => key.controlName === 'citizenship');
        if (citizenshipKey) {
          citizenshipKey.type = 'SELECT';
          citizenshipKey.options = this.countriesList.map(x => ({
            key: x.countryBDICode,
            value: x.countryDescription
          }));
        }
      }
    } else if (value === ESpecificBeneficiaryType.PERSONA_GIURIDICA) {
      this.legalPersonForm = FormUtils.initFormGroupFromTemplate(this.legalPersonFormTemplate, this.legalPersonFormValidators);
    }
    // gestione form residenza (sia PF sia PG)
    this.residenceForm = FormUtils.initFormGroupFromTemplate(residence_form_template, residence_form_validators);
    const residenceCountryKey = this.residenceFormKeys.find(key => key.controlName === 'country');
    if (residenceCountryKey) {
      residenceCountryKey.type = 'SELECT';
      residenceCountryKey.options = this.countriesList.map(x => ({
        key: x.countryBDICode,
        value: x.countryDescription
      }));
    }
    this.watchResidenceForm();
  }

  manageResidenceForeignState(isFromInputBeneficiary: boolean): void {
    this.residenceFormKeys.forEach(key => {
      if (hidden_field_foreign_state.includes(key.controlName)) {
        this.residenceForm.get(key.controlName).reset();
        this.residenceForm.get(key.controlName).disable();
        this.residenceForm.get(key.controlName).setValidators(null);
        if (key.controlName === 'place') {
          key.type = 'INPUT';
          this.residenceForm.get(key.controlName).patchValue(SIGLA_CITTA_STATO_ESTERO);
        } else if (key.controlName === 'province') {
          key.type = 'INPUT';
          this.residenceForm.get(key.controlName).patchValue(SIGLA_PROVINCIA_STATO_ESTERO);
        } else if (key.controlName === 'postalCode') {
          this.residenceForm.get(key.controlName).patchValue(SIGLA_CAP_STATO_ESTERO);
        }
      } else if (key.controlName !== 'country' && !isFromInputBeneficiary) {
        this.residenceForm.get(key.controlName).reset();
        this.residenceForm.get(key.controlName).enable();
        if (key.controlName === 'toponym') {
          this.residenceForm.get('toponym').setValidators(null);
        }
      }
    });
  }

  manageBirthForeignState(): void {
    this.birthFormKeys.forEach(key => {
      if (birth_hidden_field_foreign_state.includes(key.controlName)) {
        this.birthForm.get(key.controlName).reset();
        this.birthForm.get(key.controlName).disable();
        this.birthForm.get(key.controlName).setValidators(null);
        if (key.controlName === 'birthPlace') {
          key.type = 'INPUT';
          this.birthForm.get(key.controlName).patchValue(SIGLA_CITTA_STATO_ESTERO);
        } else if (key.controlName === 'birthProvince') {
          key.type = 'INPUT';
          this.birthForm.get(key.controlName).patchValue(SIGLA_PROVINCIA_STATO_ESTERO);
        }
      }
    });
  }

  onSearchClick(): void {
    const ndg = this.searchSubjectForm.get('ndg').value;
    LoggerService.logInfo(this.constructor.name, 'onSearchClick', ndg);
    this._spinnerService.show();
    this._policyService.retrieveNdgList(this._policyService.contractNumber, ndg)
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: response => {
          LoggerService.logInfo(this.constructor.name, 'onSearchClick', { response });
          if (response && response?.['ns2:dettaglioAnagrafico']) {
            this.searchedSubject = response['ns2:dettaglioAnagrafico'];
            if (this.searchedSubject) {
              this.additionalSubjectInfoForm = FormUtils.initFormGroupFromTemplate(additional_subject_info_form_template, additional_subject_info_form_validators);
              this.additionalSubjectInfoFormKeys = CommonUtils.assign(additional_subject_info_form_keys);
            }
          }
          this._spinnerService.hide();
        },
        error: error => {
          LoggerService.logError(this.constructor.name, 'onSearchClick', { error });
          this._alertService.openErrorMessage('Errore in fase di recupero del soggetto', 'onSearchClick', error);
          this._spinnerService.hide();
        }
      });
  }

  reset(): void {
    this.searchSubjectForm.reset();
    if (this.additionalSubjectInfoForm) {
      this.additionalSubjectInfoForm.reset();
    }
    this.searchedSubject = CommonUtils.assign(null);
  }

  onTabChange(event: MatTabChangeEvent): void {
    if (event) {
      this.selectedIndex = event.index;
    }
    LoggerService.logInfo(this.constructor.name, 'onTabChange', { selectedIndex: this.selectedIndex });
  }

  retrieveProvinces(objToPopulate: string, formKey: string): void {
    this._spinnerService.show();
    this._athoraService.retrieveProvinces()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          LoggerService.logInfo(this.constructor.name, 'retrieveProvinces', { response });
          if (response) {
            const provinceKey = this[objToPopulate].find(key => key.controlName === formKey);
            if (provinceKey) {
              provinceKey.type = 'SELECT';
              provinceKey.options = response.map(x => ({
                key: x.provinceCode,
                value: x.provinceDescription
              }));
            }
          }
          this._spinnerService.hide();
        },
        error: error => {
          LoggerService.logError(this.constructor.name, 'retrieveProvinces', { error });
          this._alertService.openErrorMessage('Errore nel recupero della lista delle province', 'retrieveProvinces', error);
          this._spinnerService.hide();
        }
      });
  }

  retrieveCities(objToPopulate: string, value: string, formKey: string): void {
    this._spinnerService.show();
    this._policyService.retrieveCities(value)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          LoggerService.logInfo(this.constructor.name, 'retrieveCities', { response });
          if (response) {
            const placeKey = this[objToPopulate].find(key => key.controlName === formKey);
            if (placeKey) {
              placeKey.type = 'SELECT';
              placeKey.options = response.map(x => ({
                key: x.cityName,
                value: x.cityName
              }));
            }
          }
          this._spinnerService.hide();
        },
        error: error => {
          LoggerService.logError(this.constructor.name, 'retrieveCities', { error });
          this._alertService.openErrorMessage('Errore nel recupero della lista delle città', 'retrieveCities', error);
          this._spinnerService.hide();
        }
      });
  }

  addNewBeneficiary(): void {
    const output = (this.selectedIndex === 0 || this.selectedIndex === undefined || !this.isSimor) ?
      {
        subjectTypeData: this.subjectTypeForm?.getRawValue(),
        individualData: this.individualForm?.getRawValue(),
        legalPersonData: this.legalPersonForm?.getRawValue(),
        residenceData: this.residenceForm?.getRawValue(),
        birthData: this.birthForm?.getRawValue(),
      }
      : this.searchedSubject;
    this.dialogRef.close({
      output: output,
      existingSubject: this.selectedIndex === 1,
      additionalFormData: this.additionalSubjectInfoForm?.getRawValue()
    });
  }

  close() {
    this.dialogRef.close();
  }

  get showIndividualForm(): boolean {
    return this.isIndividualBeneficiary && this.isSpecificBeneficiary;
  }

  get showLegalPersonForm(): boolean {
    return this.isLegalPersonBeneficiary && this.isSpecificBeneficiary;
  }

  get isSpecificBeneficiary(): boolean {
    return this.subjectTypeForm.get('beneficiaryType').value === EBeneficiaryType.SPECIFIC_BENEFICIARY;
  }

  get isIndividualBeneficiary(): boolean {
    return this.subjectTypeForm && this.subjectTypeForm.get('specificBeneficiaryType').value === ESpecificBeneficiaryType.PERSONA_FISICA;
  }

  get isLegalPersonBeneficiary(): boolean {
    return this.subjectTypeForm && this.subjectTypeForm.get('specificBeneficiaryType').value === ESpecificBeneficiaryType.PERSONA_GIURIDICA;
  }

  get isResidenceForeignState(): boolean {
    return this.residenceForm && this.residenceForm.get('country').value !== CODICE_NAZIONE_ITALIA;
  }

  get isBirthForeignState(): boolean {
    return this.birthForm && this.birthForm.get('birthCountry').value !== CODICE_NAZIONE_ITALIA;
  }

  get filteredFormKeys(): IFormKey[] {
    return this.subjectTypeFormKeys.filter(key => !key.hidden);
  }

  get isLegalSubject(): boolean {
    return this.searchedSubject && this.searchedSubject?.['ns2:naturaGiuridica'] === ESubjectType.PF ? false : true;
  }

  get subjectName(): string {
    if (this.searchedSubject) {
      if (this.isLegalSubject) {
        return `${this.searchedSubject['ns2:ragioneSocialeRiga1']}${this.searchedSubject['ns2:ragioneSocialeRiga2']}`;
      } else {
        return `${this.searchedSubject['ns2:nome']} ${this.searchedSubject['ns2:cognome']}`;
      }
    }
    return null;
  }

  get subjectKeys(): IObjectKey[] {
    if (this.searchedSubject) {
      if (this.isLegalSubject) {
        return contractor_keys_PG;
      } else {
        return contractor_keys_PF;
      }
    }
    return null;
  }

  get isConfirmButtonDisabled(): boolean {
    if (this.selectedIndex === 0 || this.selectedIndex === undefined) {
      if (this.showIndividualForm) {
        return !this.individualForm?.valid || !this.birthForm?.valid || !this.residenceForm?.valid;
      } else if (this.showLegalPersonForm) {
        return !this.legalPersonForm?.valid || !this.residenceForm?.valid;
      } else {
        return !this.subjectTypeForm?.valid;
      }
    } else {
      return CommonUtils.isVoidObj(this.searchedSubject) || !this.additionalSubjectInfoForm.valid || !this.isFiscalCodePresent;
    }
  }

  get isFiscalCodePresent(): boolean {
    if (this.isSimor && !CommonUtils.isVoidObj(this.searchedSubject)) {
      const fiscalCode: string = this.searchedSubject['ns2:codiceFiscale'];
      const vatCode: string = this.searchedSubject['ns2:partitaIva'];
      return (!CommonUtils.isVoid(fiscalCode) || !CommonUtils.isVoid(vatCode))  ? true : false;
    }
    return true;
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

}
